/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AbbiamoUiHttpConfig } from '@seller-hermes/app/types';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useStoreActions,
  useStoreState,
} from '@seller-hermes/hooks/easy-peasy';
import api from '@seller-hermes/utils/api';
import { router } from '@seller-hermes/app/globals';

export const useAbbiamoUiConfig = (): AbbiamoUiHttpConfig => {
  const { t, i18n } = useTranslation('route-planning-errors');
  const sellers = useStoreState(
    (state) => state.login.sellerGroupUser.selectedSellers,
  );

  const logout = useStoreActions((s) => s.login.logout);
  const setSelectedSellers = useStoreActions(
    (s) => s.login.sellerGroupUser.setSelectedSellers,
  );
  const useAllSellers = useStoreState(
    (s) => s.login.sellerGroupUser.useAllSellers,
  );
  const setUseAllSellers = useStoreActions(
    (s) => s.login.sellerGroupUser.setUseAllSellers,
  );
  const openSuggestionDialog = useStoreActions((s) => s.dialogs.suggest.open);
  const user_id = useStoreState((s) => s.login.sellerGroupUser.user?.id);

  const getMsgFromUserError = useCallback(
    (error: any) => {
      const msg: string = error?.response?.data?.message;
      try {
        if (String(msg).startsWith('User is already registered')) {
          return t('user-already-created');
        }
        return msg;
      } catch {
        return msg;
      }
    },
    [t],
  );
  const getMsgFromRouteError = useCallback(
    (error: any) => {
      const msg: string = error?.response?.data?.message;
      try {
        const statusErrorSplitter = 'Invoice';
        if (String(msg).startsWith(statusErrorSplitter)) {
          const invoiceInfo = msg
            .split(statusErrorSplitter)[1]
            .split('not routable')[0];
          const status = msg.split('Status: ')[1];
          return `${t('status-not-routable', {
            invoice_identifier: invoiceInfo,
            invoice_status: t(`status:${status}`),
          })}`;
        }
        return msg;
      } catch {
        return msg;
      }
    },
    [t],
  );

  const getMsgFromDeliveryError = useCallback(
    (error: any) => {
      const msg: string = error?.response?.data?.message;
      try {
        if (msg.includes('is not routeable')) {
          return `${t('order-not-routable')}`;
        }
      } finally {
      }
      return msg;
    },
    [t],
  );

  const seller_ids = useMemo(() => sellers.map((s) => s.id), [sellers]);

  const [currentPage, setCurrentPage] = useState(
    router.state.location.pathname,
  );

  useEffect(
    () =>
      router.subscribe(() => {
        setCurrentPage(router.state.location.pathname);
      }),
    [setCurrentPage],
  );

  const role_name = useStoreState((s) => s.login.sellerGroupUser.role_name);
  const user_permissions = useStoreState(
    (s) => s.login.sellerGroupUser.permissions,
  );

  return {
    createUser: async (user) =>
      api.post('/v2/users', user).then((res) => res.data),
    updateUser: async (payload) => {
      const { sellers, ...user } = payload;
      return api
        .patch(`/v2/users/${user.user_id}`, {
          ...user,
          seller_ids: sellers ? sellers.map((s) => s.id) : undefined,
        })
        .then((res) => res.data);
    },
    navbar: {
      openSuggestionDialog: () => openSuggestionDialog(),
      signoff: () => {
        logout();
      },
      currentLanguage: i18n.language,
      changeLanguage: (newLanguage: string) => {
        i18n.changeLanguage(newLanguage);
      },
      currentPage,
      changePage: (page: string, data: any) => {
        router.navigate(page, { state: data });
      },
      selectedSellers: sellers,
      setSelectedSellers: setSelectedSellers,
      useAllSellers,
      setUseAllSellers,
    },
    getSellerGroupSellers: (body) =>
      api
        .post(`/v4/search/seller_group/sellers`, { seller_ids, ...body })
        .then((v) => v.data),
    deleteDriver: () => Promise.resolve(),
    getMe: () => api.get(`/me`).then((v) => v.data),
    updateDriverRoute: async (route_id: any, driver_document: string) =>
      api.put(`v2/routes/${route_id}/driver`, {
        driver_document,
      }),
    cancelRoute: (route_id: any) =>
      api.put(`v2/routes/${route_id}/cancel`).then((v) => v.data),
    cancelationRoute: (route_id: any) =>
      api.put(`routes-page/v1/routes/${route_id}/cancel`),
    getWaypointsById: (route_id: any) => (body) =>
      api
        .post(`v4/search/route/${route_id}/deliveries`, {
          seller_ids,
          ...body,
        })
        .then((v) => v.data),
    getDeliveries: (body) =>
      api
        .post('v4/search/deliveries', { seller_ids, ...body })
        .then((v) => v.data),
    getDeliveriesCount: (body) =>
      api
        .post('v4/search/deliveries/status_count', { seller_ids, ...body })
        .then((v) => v.data),
    routesGet: (body) =>
      api
        .post('/routes-page/v1/search/routes', { seller_ids, ...body })
        .then((v) => v.data),
    getOrders: (body) =>
      api.post('v4/search/orders', { seller_ids, ...body }).then((v) => v.data),
    getRoutableOrders: (body) =>
      api
        .post('v4/search/orders', {
          seller_ids,
          ...body,
          filters: {
            ...(body.filters || {}),
            is_routeable: true,
            type: 'DELIVERY',
          },
        })
        .then((v) => v.data),
    getWarehouses: (body) =>
      api
        .post('/v4/search/warehouses', { seller_ids, ...body })
        .then((v) => v.data),
    getDrivers: (body) =>
      api
        .post('/v4/search/drivers', { seller_ids, ...body })
        .then((v) => v.data),
    createDriver: (body) =>
      api.post('/v2/driver/create', body).then((v) => v.data),
    getDriverRoute: () => api.get('/v1/driver/route').then((v) => v.data),
    createRoute: async (body) => {
      await api.post('/v2/routes', {
        invoices: body.orders.map((order) => order.id),
        driver_document_number: body.driver?.document_number,
        start_warehouse_id: body.startWarehouse?.id,
        end_warehouse_id: body.endWarehouse?.id,
      });
    },
    createDriverRoute: async (body) => {
      try {
        return api.post('/v1/driver/route', {
          invoices: body.orders.map((order) => order.id),
          start_warehouse_id: body.startWarehouse.id,
          end_warehouse_id: body.endWarehouse.id,
        });
      } catch (err: any) {
        const error = err as AxiosError;
        if (error.response?.status === 500) {
          return t('retry-again-later');
        }
        if (typeof err?.response?.data?.message === 'string') {
          const msg = getMsgFromRouteError(err);
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    createDelivery: async (body) => {
      try {
        const res = await api
          .post('/v2/deliveries', {
            invoice_id: body.order.id,
            warehouse_id: body.warehouse.id,
            carrier_type: body.carrier_type,
            carrier_method_id: body.method_id,
          })
          .then((v) => v.data);
        return res;
      } catch (err) {
        const error = err as AxiosError<any>;
        if (error.response?.status === 500) {
          throw new Error(t('retry-again-later'));
        }
        if (typeof error?.response?.data?.message === 'string') {
          const msg = getMsgFromDeliveryError(err);
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    getCarriers: (body) =>
      api.post('v4/search/carriers', body).then((v) => v.data),
    getCarriersIntegrations: (body) =>
      api.post('v4/search/carriers-integrations', body).then((v) => v.data),
    updateOrdersToReadyForTakeout: async ({ invoice_ids }) => {
      try {
        return api
          .post('/orders/takeout-ready', { user_id, invoice_ids })
          .then((res) => res.data);
      } catch (err) {
        const error = err as AxiosError<any>;
        if (error.response?.status === 500) {
          return t('retry-again-later');
        }

        if (typeof error?.response?.data?.message === 'string') {
          const msg = error?.response?.data?.message;
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    updateOrderToWithdrawn: async ({ invoice_id, receiver, attachments }) => {
      const formData = new FormData();

      if (attachments) {
        for (const fileWithPreview of attachments) {
          if (fileWithPreview.file instanceof File) {
            formData.append('attachments', fileWithPreview.file);
          }
        }
      }

      formData.append('invoice_id', invoice_id);

      if (receiver?.name) {
        formData.append('name', receiver?.name);
      }

      if (receiver?.document_number) {
        formData.append('document_number', receiver?.document_number);
      }

      if (receiver?.description) {
        formData.append('description', receiver?.description);
      }

      try {
        return api.post('/order/withdrawn', formData).then((res) => res.data);
      } catch (err) {
        const error = err as AxiosError<any>;
        if (error.response?.status === 500) {
          return t('retry-again-later');
        }

        if (typeof error?.response?.data?.message === 'string') {
          const msg = error?.response?.data?.message;
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    cancelOrderWithdraw: async ({ invoice_id }) => {
      try {
        return api
          .post('/order/cancel-takeout', { invoice_id })
          .then((res) => res.data);
      } catch (err) {
        const error = err as AxiosError<any>;
        if (error.response?.status === 500) {
          return t('retry-again-later');
        }

        if (typeof error?.response?.data?.message === 'string') {
          const msg = error?.response?.data?.message;
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    updateOrderToReturned: async (body) => {
      try {
        return api.post('/delivery/returned', body).then((res) => res.data);
      } catch (err) {
        const error = err as AxiosError<any>;
        if (error.response?.status === 500) {
          return t('retry-again-later');
        }

        if (typeof error?.response?.data?.message === 'string') {
          const msg = error?.response?.data?.message;
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    cancelInvoiceDelivery: async ({ invoice_id }) => {
      try {
        return api
          .post('/delivery/cancel', { invoice_id })
          .then((res) => res.data);
      } catch (err) {
        const error = err as AxiosError<any>;
        if (error.response?.status === 500) {
          return t('retry-again-later');
        }

        if (typeof error?.response?.data?.message === 'string') {
          const msg = error?.response?.data?.message;
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    getSellerInfo: async ({ seller_id }) => {
      try {
        return api
          .post(`/search/seller`, { seller_id })
          .then((res) => res.data);
      } catch (err) {
        const error = err as AxiosError<any>;
        if (error.response?.status === 500) {
          return t('retry-again-later');
        }
        if (typeof error?.response?.data?.message === 'string') {
          const msg = error?.response?.data?.message;
          throw new Error(msg);
        }
        throw new Error(t('retry-again-later'));
      }
    },
    permissions: user_permissions,
    role_name,
  } as AbbiamoUiHttpConfig;
};
